import {
  AppstoreOutlined,
  BarChartOutlined,
  BulbOutlined,
  CalculatorOutlined,
  CloudOutlined,
  EnvironmentOutlined,
  ExclamationOutlined,
  FilterOutlined,
  FontSizeOutlined,
  FormOutlined,
  NumberOutlined,
  PushpinOutlined,
  SplitCellsOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { DrilldownPanelProps } from 'features/drilldown/components/DrilldownPanel'
import { DrilldownTableConfigContextProviderProps } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import {
  ActiveFiltersPanel,
  DEFAULT_ACTIVE_FILTERS_PANEL_DATA,
} from 'features/drilldown/panels/global/activeFilters/ActiveFiltersPanel'
import {
  BulkEditPanel,
  DEFAULT_BULK_EDIT_PANEL_DATA,
} from 'features/drilldown/panels/global/bulkEdit'
import {
  CardsPanel,
  DEFAULT_CARDS_PANEL_DATA,
} from 'features/drilldown/panels/global/cards/CardsPanel'
import { CardsPanelConfigurationView } from 'features/drilldown/panels/global/cards/CardsPanelConfigurationView'
import { DEFAULT_COLUMN_BREAK_PANEL_DATA } from 'features/drilldown/panels/global/columnBreak/columnBreak'
import { DrilldownCountsConfigView } from 'features/drilldown/panels/global/countsView/DrilldownCountsConfigView'
import {
  DEFAULT_COUNTS_PANEL_DATA,
  DrilldownCountsView,
} from 'features/drilldown/panels/global/countsView/DrilldownCountsView'
import {
  DEFAULT_FILTER_PANEL_DATA,
  DrilldownFilterPanelView,
} from 'features/drilldown/panels/global/filters/DrilldownFilterPanel'
import {
  ASSET_HISTOGRAM_CONFIG_PROPS,
  DEFAULT_ASSET_HISTOGRAM_PANEL_DATA,
} from 'features/drilldown/panels/global/histogramPresets/assetHistogram'
import {
  ASSET_TYPE_HISTOGRAM_CONFIG_PROPS,
  DEFAULT_ASSET_TYPE_HISTOGRAM_PANEL_DATA,
} from 'features/drilldown/panels/global/histogramPresets/assetTypeHistogram'
import {
  DEFAULT_INDICATION_CLASSIFICATION_HISTOGRAM_PANEL_DATA,
  INDICATION_CLASSIFICATION_HISTOGRAM_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/indicationClassificationHistogram'
import {
  DEFAULT_INDICATION_DAMAGE_MECHANISM_HISTOGRAM_PANEL_DATA,
  INDICATION_DAMAGE_MECHANISM_HISTOGRAM_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/indicationDamageMechanismHistogram'
import {
  DEFAULT_INSPECTION_TIMELINE_PANEL_DATA,
  INSPECTION_TIMELINE_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/inspectionTimeline'
import {
  DEFAULT_INSPECTION_TYPE_HISTOGRAM_PANEL_DATA,
  INSPECTION_TYPE_HISTOGRAM_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/inspectionTypeHistogram'
import {
  DEFAULT_SITE_HISTOGRAM_PANEL_DATA,
  SITE_HISTOGRAM_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/siteHistogram'
import {
  DEFAULT_WORK_ORDER_HISTOGRAM_PANEL_DATA,
  WORK_ORDER_HISTOGRAM_CONFIG_PROPS,
} from 'features/drilldown/panels/global/histogramPresets/workOrderHistogram'
import {
  DEFAULT_INDICATION_PANEL_DATA,
  IndicationListPanel,
} from 'features/drilldown/panels/global/indicationList/IndicationListPanel'
import {
  DEFAULT_INDICATION_TABLE_PANEL_DATA,
  INDICATION_TABLE_EXTRA_COLS,
} from 'features/drilldown/panels/global/indicationTable/IndicationTable'
import {
  DEFAULT_INSPECTION_TABLE_PANEL_DATA,
  INSPECTION_TABLE_EXTRA_COL_DEFS,
  INSPECTION_TABLE_INITIAL_SORTING_OPTIONS,
} from 'features/drilldown/panels/global/inspectionTable/InspectionTable'
import {
  DEFAULT_LLM_PANEL_DATA,
  LlmPanel,
} from 'features/drilldown/panels/global/llmPanel/LlmPanel'
import {
  DEFAULT_MARKDOWN_PANEL_DATA,
  MarkdownPanel,
} from 'features/drilldown/panels/global/markdown/MarkdownPanel'
import {
  DEFAULT_METRICS_PANEL_DATA,
  MetricsPanel,
} from 'features/drilldown/panels/global/metrics/MetricsPanel'
import { MetricsPanelConfigView } from 'features/drilldown/panels/global/metrics/MetricsPanelConfigView'
import {
  DEFAULT_POINT_CLOUD_PANEL_DATA,
  PointCloud,
} from 'features/drilldown/panels/global/pointCloud/PointCloud'
import {
  DEFAULT_MAP_PANEL_DATA,
  DrilldownSiteLocationsMap,
} from 'features/drilldown/panels/global/siteLocationsMap/DrilldownSiteLocationsMap'
import { DEFAULT_SITE_TABLE_PANEL_DATA } from 'features/drilldown/panels/global/siteTable/SiteTable'
import {
  DEFAULT_SITE_PANEL_DATA,
  SiteView,
} from 'features/drilldown/panels/global/siteView/SiteView'
import { DEFAULT_BLANK_TABLE_PANEL_DATA } from 'features/drilldown/panels/global/table/Table'
import {
  DEFAULT_UNIT_TABLE_PANEL_DATA,
  UNIT_TABLE_EXTRA_COLS,
} from 'features/drilldown/panels/global/unitTable/UnitTable'
import {
  DEFAULT_FFS_RESULT_TABLE_PANEL_DATA,
  FFS_RESULT_TABLE_EXTRA_COLS,
} from 'features/drilldown/panels/impl/ffsResultTable/FfsResultTable'
import {
  DEFAULT_NII_RESULTS_BY_ZONE_PANEL_DATA,
  NII_LIBRA_RESULTS_BY_ZONE_TITLE_TOOLTIP,
  NiiLibraResultsByZonePanel,
} from 'features/drilldown/panels/impl/niiLibraResultsByZoneTable/NiiLibraResultsByZonePanel'
import {
  DEFAULT_NII_RESULTS_PANEL_DATA,
  NII_LIBRA_RESULTS_TITLE_TOOLTIP,
  NiiLibraResultsPanel,
} from 'features/drilldown/panels/impl/niiLibraResultsTable/NiiLibraResultsPanel'
import {
  DEFAULT_NII_ROI_PANEL_DATA,
  NiiRoiPanel,
} from 'features/drilldown/panels/impl/niiRoi/NiiRoiPanel'
import {
  DEFAULT_RBI_MATRIX_PANEL_DATA,
  RbiMatrix,
} from 'features/drilldown/panels/impl/rbiMatrix/RbiMatrix'
import { DrilldownPanelType } from 'features/drilldown/types'
import { FC, ReactNode } from 'react'
import CantileverTheme from 'styles/cantileverTheme'

import {
  createPanelConfig,
  HistogramPanelRender,
  TablePanelRender,
} from './PanelRenderUtils'

export type DrilldownPanelInitialDataMap = {
  map: typeof DEFAULT_MAP_PANEL_DATA
  filter: typeof DEFAULT_FILTER_PANEL_DATA
  activeFilters: typeof DEFAULT_ACTIVE_FILTERS_PANEL_DATA
  counts: typeof DEFAULT_COUNTS_PANEL_DATA
  siteView: typeof DEFAULT_SITE_PANEL_DATA
  table: typeof DEFAULT_BLANK_TABLE_PANEL_DATA
  unitTable: typeof DEFAULT_UNIT_TABLE_PANEL_DATA
  siteTable: typeof DEFAULT_SITE_TABLE_PANEL_DATA
  siteHistogram: typeof DEFAULT_SITE_HISTOGRAM_PANEL_DATA
  inspectionTimeline: typeof DEFAULT_INSPECTION_TIMELINE_PANEL_DATA
  inspectionTypeHistogram: typeof DEFAULT_INSPECTION_TYPE_HISTOGRAM_PANEL_DATA
  assetHistogram: typeof DEFAULT_ASSET_HISTOGRAM_PANEL_DATA
  assetTypeHistogram: typeof DEFAULT_ASSET_TYPE_HISTOGRAM_PANEL_DATA
  indicationDamageMechanismHistogram: typeof DEFAULT_INDICATION_DAMAGE_MECHANISM_HISTOGRAM_PANEL_DATA
  indicationClassificationHistogram: typeof DEFAULT_INDICATION_CLASSIFICATION_HISTOGRAM_PANEL_DATA
  indicationList: typeof DEFAULT_INDICATION_PANEL_DATA
  indicationTable: typeof DEFAULT_INDICATION_TABLE_PANEL_DATA
  ffsResultTable: typeof DEFAULT_FFS_RESULT_TABLE_PANEL_DATA
  rbiMatrix: typeof DEFAULT_RBI_MATRIX_PANEL_DATA
  inspectionTable: typeof DEFAULT_INSPECTION_TABLE_PANEL_DATA
  columnBreak: typeof DEFAULT_COLUMN_BREAK_PANEL_DATA
  llmPanel: typeof DEFAULT_LLM_PANEL_DATA
  markdown: typeof DEFAULT_MARKDOWN_PANEL_DATA
  metrics: typeof DEFAULT_METRICS_PANEL_DATA
  niiLibraResultsByZonePanel: typeof DEFAULT_NII_RESULTS_BY_ZONE_PANEL_DATA
  niiLibraResultsPanel: typeof DEFAULT_NII_RESULTS_PANEL_DATA
  niiRoiPanel: typeof DEFAULT_NII_ROI_PANEL_DATA
  bulkEdit: typeof DEFAULT_BULK_EDIT_PANEL_DATA
  pointCloud: typeof DEFAULT_POINT_CLOUD_PANEL_DATA
  cards: typeof DEFAULT_CARDS_PANEL_DATA
  workOrderHistogram: typeof DEFAULT_WORK_ORDER_HISTOGRAM_PANEL_DATA
}

export const DRILLDOWN_PANEL_INITIAL_DATA: DrilldownPanelInitialDataMap = {
  map: DEFAULT_MAP_PANEL_DATA,
  filter: DEFAULT_FILTER_PANEL_DATA,
  activeFilters: DEFAULT_ACTIVE_FILTERS_PANEL_DATA,
  counts: DEFAULT_COUNTS_PANEL_DATA,
  siteView: DEFAULT_SITE_PANEL_DATA,
  table: DEFAULT_BLANK_TABLE_PANEL_DATA,
  unitTable: DEFAULT_UNIT_TABLE_PANEL_DATA,
  siteTable: DEFAULT_SITE_TABLE_PANEL_DATA,
  siteHistogram: DEFAULT_SITE_HISTOGRAM_PANEL_DATA,
  inspectionTimeline: DEFAULT_INSPECTION_TIMELINE_PANEL_DATA,
  inspectionTypeHistogram: DEFAULT_INSPECTION_TYPE_HISTOGRAM_PANEL_DATA,
  assetHistogram: DEFAULT_ASSET_HISTOGRAM_PANEL_DATA,
  assetTypeHistogram: DEFAULT_ASSET_TYPE_HISTOGRAM_PANEL_DATA,
  indicationDamageMechanismHistogram:
    DEFAULT_INDICATION_DAMAGE_MECHANISM_HISTOGRAM_PANEL_DATA,
  indicationClassificationHistogram:
    DEFAULT_INDICATION_CLASSIFICATION_HISTOGRAM_PANEL_DATA,
  indicationList: DEFAULT_INDICATION_PANEL_DATA,
  indicationTable: DEFAULT_INDICATION_TABLE_PANEL_DATA,
  ffsResultTable: DEFAULT_FFS_RESULT_TABLE_PANEL_DATA,
  rbiMatrix: DEFAULT_RBI_MATRIX_PANEL_DATA,
  inspectionTable: DEFAULT_INSPECTION_TABLE_PANEL_DATA,
  markdown: DEFAULT_MARKDOWN_PANEL_DATA,
  metrics: DEFAULT_METRICS_PANEL_DATA,
  columnBreak: DEFAULT_COLUMN_BREAK_PANEL_DATA,
  llmPanel: DEFAULT_LLM_PANEL_DATA,
  niiLibraResultsByZonePanel: DEFAULT_NII_RESULTS_BY_ZONE_PANEL_DATA,
  niiLibraResultsPanel: DEFAULT_NII_RESULTS_PANEL_DATA,
  niiRoiPanel: DEFAULT_NII_ROI_PANEL_DATA,
  bulkEdit: DEFAULT_BULK_EDIT_PANEL_DATA,
  pointCloud: DEFAULT_POINT_CLOUD_PANEL_DATA,
  cards: DEFAULT_CARDS_PANEL_DATA,
  workOrderHistogram: DEFAULT_WORK_ORDER_HISTOGRAM_PANEL_DATA,
}

export type DrilldownPanelConfig = {
  name: DrilldownPanelType
  icon: ReactNode
  tooltip?: string
  render?: FC<DrilldownPanelProps>
  isGloballyAvailable: boolean
}

function createTablePanelConfig(
  panelType: DrilldownPanelType,
  configProps: Omit<DrilldownTableConfigContextProviderProps, 'children'> = {
    allowSetColumns: true,
    allowSetGroupByType: false,
    extraColumns: [],
  }
): DrilldownPanelConfig {
  return {
    name: panelType,
    icon: <TableOutlined />,
    isGloballyAvailable: true,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA[panelType].title,
    render: (props) => (
      <TablePanelRender
        panelProps={{ ...props, panelPadding: 0 }}
        configProps={configProps}
      />
    ),
  }
}

export const DRILLDOWN_PANEL_CONFIGS: Record<
  DrilldownPanelType,
  DrilldownPanelConfig
> = {
  map: createPanelConfig(
    'map',
    DRILLDOWN_PANEL_INITIAL_DATA.map,
    DrilldownSiteLocationsMap,
    {
      icon: <PushpinOutlined />,
      panelPadding: 0,
      isGloballyAvailable: true,
    }
  ),
  filter: createPanelConfig(
    'filter',
    DRILLDOWN_PANEL_INITIAL_DATA.filter,
    DrilldownFilterPanelView,
    {
      icon: <FilterOutlined />,
      isGloballyAvailable: true,
    }
  ),
  activeFilters: createPanelConfig(
    'activeFilters',
    DRILLDOWN_PANEL_INITIAL_DATA.activeFilters,
    ActiveFiltersPanel,
    {
      icon: <FilterOutlined />,
      panelPadding: 0,
      isGloballyAvailable: true,
    }
  ),
  counts: createPanelConfig(
    'counts',
    DRILLDOWN_PANEL_INITIAL_DATA.counts,
    DrilldownCountsView,
    {
      icon: <NumberOutlined />,
      configPanel: <DrilldownCountsConfigView />,
      isGloballyAvailable: true,
    }
  ),
  siteView: createPanelConfig(
    'siteView',
    DRILLDOWN_PANEL_INITIAL_DATA.siteView,
    SiteView,
    {
      icon: <EnvironmentOutlined />,
      panelPadding: 0,
      isGloballyAvailable: true,
    }
  ),
  table: createTablePanelConfig('table', {
    allowSetColumns: true,
    allowSetGroupByType: true,
    extraColumns: [],
  }),
  unitTable: createTablePanelConfig('unitTable', {
    extraColumns: UNIT_TABLE_EXTRA_COLS,
    allowSetColumns: true,
    allowSetGroupByType: true,
  }),
  siteTable: createTablePanelConfig('siteTable', {
    allowSetGroupByType: true,
    allowSetColumns: true,
    extraColumns: [],
  }),
  siteHistogram: {
    name: 'siteHistogram',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.siteHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={SITE_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  inspectionTimeline: {
    name: 'inspectionTimeline',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.inspectionTimeline.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={INSPECTION_TIMELINE_CONFIG_PROPS}
      />
    ),
  },
  inspectionTypeHistogram: {
    name: 'inspectionTypeHistogram',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.inspectionTypeHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={INSPECTION_TYPE_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  assetHistogram: {
    name: 'assetHistogram',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.assetHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={ASSET_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  assetTypeHistogram: {
    name: 'assetTypeHistogram',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.assetTypeHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={ASSET_TYPE_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  indicationDamageMechanismHistogram: {
    name: 'indicationDamageMechanismHistogram',
    icon: <BarChartOutlined />,
    tooltip:
      DRILLDOWN_PANEL_INITIAL_DATA.indicationDamageMechanismHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={INDICATION_DAMAGE_MECHANISM_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  indicationClassificationHistogram: {
    name: 'indicationClassificationHistogram',
    icon: <BarChartOutlined />,
    tooltip:
      DRILLDOWN_PANEL_INITIAL_DATA.indicationClassificationHistogram.title,
    isGloballyAvailable: true,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={INDICATION_CLASSIFICATION_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
  indicationList: createPanelConfig(
    'indicationList',
    DRILLDOWN_PANEL_INITIAL_DATA.indicationList,
    IndicationListPanel,
    {
      icon: <ExclamationOutlined />,
      isGloballyAvailable: true,
    }
  ),
  indicationTable: createTablePanelConfig('indicationTable', {
    allowSetColumns: true,
    allowSetGroupByType: true,
    extraColumns: INDICATION_TABLE_EXTRA_COLS,
  }),
  ffsResultTable: {
    ...createTablePanelConfig('ffsResultTable', {
      allowSetColumns: true,
      allowSetGroupByType: true,
      extraColumns: FFS_RESULT_TABLE_EXTRA_COLS,
    }),
    isGloballyAvailable: false,
  },
  rbiMatrix: createPanelConfig(
    'rbiMatrix',
    DRILLDOWN_PANEL_INITIAL_DATA.rbiMatrix,
    RbiMatrix,
    {
      isGloballyAvailable: false,
    }
  ),
  inspectionTable: createTablePanelConfig('inspectionTable', {
    extraColumns: INSPECTION_TABLE_EXTRA_COL_DEFS,
    allowSetColumns: true,
    allowSetGroupByType: true,
    initialSortingOpts: INSPECTION_TABLE_INITIAL_SORTING_OPTIONS,
  }),
  niiLibraResultsByZonePanel: createPanelConfig(
    'niiLibraResultsByZonePanel',
    DRILLDOWN_PANEL_INITIAL_DATA.niiLibraResultsByZonePanel,
    NiiLibraResultsByZonePanel,
    {
      titleTooltip: NII_LIBRA_RESULTS_BY_ZONE_TITLE_TOOLTIP,
      isGloballyAvailable: false,
      panelPadding: 0,
    }
  ),
  niiLibraResultsPanel: createPanelConfig(
    'niiLibraResultsPanel',
    DRILLDOWN_PANEL_INITIAL_DATA.niiLibraResultsPanel,
    NiiLibraResultsPanel,
    {
      titleTooltip: NII_LIBRA_RESULTS_TITLE_TOOLTIP,
      isGloballyAvailable: false,
      panelPadding: 0,
    }
  ),
  niiRoiPanel: createPanelConfig(
    'niiRoiPanel',
    DRILLDOWN_PANEL_INITIAL_DATA.niiRoiPanel,
    NiiRoiPanel,
    {
      isGloballyAvailable: false,
      panelPadding: 0,
    }
  ),
  markdown: createPanelConfig(
    'markdown',
    DRILLDOWN_PANEL_INITIAL_DATA.markdown,
    MarkdownPanel,
    {
      icon: <FontSizeOutlined />,
      isGloballyAvailable: true,
    }
  ),
  metrics: createPanelConfig(
    'metrics',
    DRILLDOWN_PANEL_INITIAL_DATA.metrics,
    MetricsPanel,
    {
      icon: <CalculatorOutlined />,
      configPanel: <MetricsPanelConfigView />,
      isGloballyAvailable: true,
    }
  ),
  llmPanel: createPanelConfig(
    'llmPanel',
    DRILLDOWN_PANEL_INITIAL_DATA.llmPanel,
    LlmPanel,
    {
      icon: (
        <BulbOutlined style={{ color: CantileverTheme.colors.purple['400'] }} />
      ),
      panelPadding: 0,
      isGloballyAvailable: true,
    }
  ),
  columnBreak: {
    name: 'columnBreak',
    icon: <SplitCellsOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.columnBreak.title,
    isGloballyAvailable: true,
  },
  bulkEdit: createPanelConfig(
    'bulkEdit',
    DRILLDOWN_PANEL_INITIAL_DATA.bulkEdit,
    BulkEditPanel,
    {
      icon: <FormOutlined />,
      isGloballyAvailable: true,
    }
  ),
  pointCloud: createPanelConfig(
    'pointCloud',
    DRILLDOWN_PANEL_INITIAL_DATA.pointCloud,
    PointCloud,
    {
      icon: <CloudOutlined />,
      panelPadding: 0,
      isGloballyAvailable: true,
    }
  ),
  cards: createPanelConfig(
    'cards',
    DRILLDOWN_PANEL_INITIAL_DATA.cards,
    CardsPanel,
    {
      icon: <AppstoreOutlined />,
      panelPadding: 0,
      configPanel: <CardsPanelConfigurationView />,
      isGloballyAvailable: true,
    }
  ),
  workOrderHistogram: {
    name: 'workOrderHistogram',
    icon: <BarChartOutlined />,
    tooltip: DRILLDOWN_PANEL_INITIAL_DATA.workOrderHistogram.title,
    isGloballyAvailable: false,
    render: (props) => (
      <HistogramPanelRender
        panelProps={props}
        histoConfigProps={WORK_ORDER_HISTOGRAM_CONFIG_PROPS}
      />
    ),
  },
}
